import { Record as record } from 'immutable';

import config from '../../../src/config';
import trainTime from '../../../src/lib/TrainTime';
import { construct, resolve, fields } from '../../../src/helpers/LinkMapper';
import { getDefaults, getResolvers } from '../../../src/helpers/Records';
import BaseStore from '../../../src/stores/BaseStore';
import { ActionTypes } from '../../../src/constants/AppConstants';
import WhitelistClaim from '../records/compensation/WhitelistClaim';
import SeasonClaim from '../records/compensation/SeasonClaim';
import NonSeasonClaim from '../records/compensation/NonSeasonClaim';

const stateSchema = {
    whitelist: [ resolve.as(WhitelistClaim), new WhitelistClaim() ],
    season: [ resolve.as(SeasonClaim), new SeasonClaim() ],
    nonSeason: [ resolve.as(NonSeasonClaim), NonSeasonClaim.create({
        startDate: trainTime(config.get('compensationNonSeasonStartDate')),
        endDate: trainTime(config.get('compensationNonSeasonEndDate')),
        startDayOfWeek: config.get('compensationStartDayOfWeek')
    }) ],
    isComplete: [ fields.bool, false ]
};

class CompensationState extends record(getDefaults(stateSchema)) {
    static fromJSON(json = {}) {
        return construct(CompensationState, json, getResolvers(stateSchema));
    }
}

export class CompensationStore extends BaseStore {

    constructor(options = {}) {
        super({
            recordType: CompensationState,
            storageKey: 'compensation',
            expires: 60 * 60 * 24 * 7,
            ...options
        });
    }

    serialize() {
        /*
          For now, don't persist season claims. If enabling later, consider
          expiry time & whether to persist data for logged-in users only,
          like non-season claims.
         */
        return this.state.set('isComplete', false)
            .delete('whitelist')
            .delete('season')
            .withMutations(store => {
                if (store.nonSeason.hasTickets()) {
                    return store.set('nonSeason', store.nonSeason.delete('payment'));
                }
                return store.delete('nonSeason');
            })
            .toJS();
    }

    handleUserAdded ({ claimType, user }) {
        if (user.isFullyLoaded()) {
            const {[claimType]: claim} = this.state;
            this.setState(
                this.state.set(claimType, claim.setUser(user))
            );
        }
    }

    handleClaimAborted ({ claimType }) {
        this.setState(this.state.delete(claimType));
    }

    handleSmartcardAdded ({ claimType, smartcard }) {
        const {[claimType]: claim} = this.state;
        this.setState(
            this.state.set(claimType, claim.setSmartcard(smartcard))
        );
    }

    handleWhitelistInitialised ({ email, passcode }) {
        const { customer } = this.state.whitelist;
        this.setState(
            this.state.setIn(
                ['whitelist', 'customer'],
                customer.merge({ email, passcode })
            )
        );
    }

    handleWhitelistValidated ({ claimDetails }) {
        this.setState(this.state.setIn(['whitelist', 'claimDetails'], claimDetails));
    }

    handleClaimUpdate({ claimType, field, value }) {
        const updated = this.state.setIn([claimType].concat(field), value);
        return this.setState(updated);
    }

    handleAddRoute () {
        const { season } = this.state;
        this.setState(this.state.set('season', season.addRoute()));
    }

    handleRemoveRoute ({ route }) {
        const { season } = this.state;
        this.setState(this.state.set('season', season.removeRoute(route)));
    }


    handleAddTicket ({ claimType, route }) {
        const claim = this.state.get(claimType);
        this.setState(this.state.set(claimType, claim.addTicket(route)));
    }

    handleRemoveTicket ({ claimType, route, ticket }) {
        const claim = this.state.get(claimType);
        this.setState(this.state.set(claimType, claim.removeTicket(route, ticket)));
    }

    handleAddTravelWeek() {
        const { nonSeason } = this.state;
        this.setState(
            this.state.set('nonSeason', nonSeason.addTravelWeek())
        );
    }

    handleRemoveTravelWeek({ travelWeek }) {
        const { nonSeason } = this.state;
        this.setState(
            this.state.set('nonSeason', nonSeason.removeTravelWeek(travelWeek))
        );
    }

    handleUpdateTravelWeek({ travelWeek }) {
        const { nonSeason } = this.state;
        this.setState(
            this.state.set(
                'nonSeason',
                nonSeason.updateTravelWeek(travelWeek)
            )
        );
    }

    handleUploadStarted ({ claimType, context, form, file, xhr }) {
        const claim = this.state.get(claimType);
        const updatedClaim = claim.updateEvidence(context, form, {
            file,
            xhr,
            uploadFailed: false,
            imageId: null
        });
        this.setState(this.state.set(claimType, updatedClaim));
    }

    handleUploadComplete ({ claimType, context, form, imageId }) {
        const claim = this.state.get(claimType);
        const updatedClaim = claim.updateEvidence(context, form, {
            imageId,
            xhr: null
        });
        this.setState(this.state.set(claimType, updatedClaim));
    }

    handleUploadFailed ({ claimType, context, form }) {
        const claim = this.state.get(claimType);
        const updatedClaim = claim.updateEvidence(context, form, {
            xhr: null,
            uploadFailed: true
        });
        this.setState(this.state.set(claimType, updatedClaim));
    }

    handleUploadCancelled ({ claimType, context, form }) {
        const claim = this.state.get(claimType);
        const updatedClaim = claim.updateEvidence(context, form, {
            xhr: null,
            file: null,
            imageId: null,
            uploadFailed: false
        });
        this.setState(this.state.set(claimType, updatedClaim));
    }

    handleAuthorized ({ claimType }) {
        this.setState(
            this.state.delete(claimType).set('isComplete', true)
        );
    }

    getActionHandlers () {
        return {
            [ActionTypes.COMPENSATION_RESET]: this.resetState,
            [ActionTypes.COMPENSATION_CLAIM_UPDATED]: this.handleClaimUpdate,
            [ActionTypes.COMPENSATION_CLAIM_ABORTED]: this.handleClaimAborted,
            [ActionTypes.COMPENSATION_ROUTE_ADDED]: this.handleAddRoute,
            [ActionTypes.COMPENSATION_ROUTE_REMOVED]: this.handleRemoveRoute,
            [ActionTypes.COMPENSATION_TICKET_ADDED]: this.handleAddTicket,
            [ActionTypes.COMPENSATION_TICKET_REMOVED]: this.handleRemoveTicket,
            [ActionTypes.COMPENSATION_TRAVEL_WEEK_ADDED]: this.handleAddTravelWeek,
            [ActionTypes.COMPENSATION_TRAVEL_WEEK_REMOVED]: this.handleRemoveTravelWeek,
            [ActionTypes.COMPENSATION_TRAVEL_WEEK_UPDATED]: this.handleUpdateTravelWeek,
            [ActionTypes.COMPENSATION_UPLOAD_STARTING]: this.handleUploadStarted,
            [ActionTypes.COMPENSATION_UPLOAD_COMPLETE]: this.handleUploadComplete,
            [ActionTypes.COMPENSATION_UPLOAD_FAILED]: this.handleUploadFailed,
            [ActionTypes.COMPENSATION_UPLOAD_CANCELLED]: this.handleUploadCancelled,
            [ActionTypes.COMPENSATION_WHITELIST_INITIALISED]: this.handleWhitelistInitialised,
            [ActionTypes.COMPENSATION_WHITELIST_VALIDATED]: this.handleWhitelistValidated,
            [ActionTypes.COMPENSATION_SUBMITTED]: this.handleAuthorized,
            [ActionTypes.COMPENSATION_USER_ADDED]: this.handleUserAdded,
            [ActionTypes.COMPENSATION_SMARTCARD_ADDED]: this.handleSmartcardAdded,
            [ActionTypes.LOGGED_OUT]: this.resetState
        };
    }
}

const instance = new CompensationStore();

export default instance;
